import { useMemo } from 'react';
import { TableStateHookParams, TableStateHookReturn } from '../../types';

/**
 * Hook for managing overall table state (loading, error, data)
 *
 * This hook determines the current state of the table based on loading
 * and error conditions, providing flags for what UI elements to show.
 *
 * @template T - The type of data rows in the table
 * @param params - Configuration object containing:
 *   - isLoading: Whether data is currently being loaded
 *   - error: Current error state, if any
 *
 * @returns Object containing:
 *   - currentState: Current state ('loading' | 'error' | 'data')
 *   - shouldShowData: Whether to show table data
 *   - shouldShowLoading: Whether to show loading indicator
 *   - shouldShowError: Whether to show error state
 *   - errorToDisplay: Error to display if in error state
 *
 * @example
 * ```tsx
 * const {
 *   currentState,
 *   shouldShowData,
 *   shouldShowLoading,
 *   shouldShowError,
 *   errorToDisplay
 * } = useTableState({
 *   isLoading: fetchingData,
 *   error: fetchError
 * });
 *
 * // Use flags to conditionally render UI
 * {shouldShowLoading && <LoadingSpinner />}
 * {shouldShowError && <ErrorMessage error={errorToDisplay} />}
 * {shouldShowData && <TableData />}
 * ```
 */
export function useTableState<T extends Record<string, unknown>>({
  isLoading = false,
  error,
}: TableStateHookParams<T>): TableStateHookReturn {
  return useMemo(() => {
    // Error state takes priority
    if (error) {
      return {
        currentState: 'error',
        shouldShowData: false,
        shouldShowLoading: false,
        shouldShowError: true,
        errorToDisplay: error,
      };
    }

    // Loading state when explicitly loading or when loading with no data
    if (isLoading) {
      return {
        currentState: 'loading',
        shouldShowData: false,
        shouldShowLoading: true,
        shouldShowError: false,
      };
    }

    // Default to data state
    return {
      currentState: 'data',
      shouldShowData: true,
      shouldShowLoading: false,
      shouldShowError: false,
    };
  }, [isLoading, error]);
}
